import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Target,
  Shield,
  DollarSign,
  Building,
  ChevronRight,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Clock,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const FundingReadiness = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [fundingData, setFundingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFundingData();
  }, []);

  const fetchFundingData = async () => {
    try {
      const [scoreRes, strategyRes] = await Promise.all([
        axios.get(`${API}/funding/score`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/funding/strategy`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      
      setFundingData({
        ...scoreRes.data,
        strategy: strategyRes.data
      });
    } catch (error) {
      console.error('Funding data error:', error);
      toast.error('Failed to load funding data');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 75) return 'bg-emerald-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="funding-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="funding-readiness">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Funding Readiness</h1>
          <p className="text-sm text-stone-500">AI-powered analysis of your funding approval chances</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchFundingData}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Main Score Card */}
      <div className="elite-card p-8">
        <div className="flex items-center gap-8">
          {/* Score Gauge */}
          <div className="relative w-40 h-40">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="80"
                cy="80"
                r="70"
                fill="none"
                stroke="#E2E8F0"
                strokeWidth="12"
              />
              <circle
                cx="80"
                cy="80"
                r="70"
                fill="none"
                stroke={fundingData?.score >= 75 ? '#10B981' : fundingData?.score >= 50 ? '#F59E0B' : '#EF4444'}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 70}
                strokeDashoffset={2 * Math.PI * 70 * (1 - (fundingData?.score || 0) / 100)}
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-4xl font-bold ${getScoreColor(fundingData?.score || 0)}`}>
                {fundingData?.score || 0}%
              </span>
              <span className="text-sm text-stone-500">Readiness</span>
            </div>
          </div>

          {/* Status Details */}
          <div className="flex-1">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
              fundingData?.status === 'ready' ? 'bg-emerald-100 text-emerald-700' :
              fundingData?.status === 'needs_work' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {fundingData?.status === 'ready' ? (
                <CheckCircle size={18} />
              ) : fundingData?.status === 'needs_work' ? (
                <Clock size={18} />
              ) : (
                <AlertTriangle size={18} />
              )}
              <span className="font-semibold">
                {fundingData?.status === 'ready' ? 'Ready for Funding' :
                 fundingData?.status === 'needs_work' ? 'Needs Improvement' : 'Not Ready'}
              </span>
            </div>
            <p className="mt-4 text-stone-600 max-w-md">
              {fundingData?.status === 'ready' 
                ? 'Your credit profile meets most lender requirements. You have a good chance of approval for business funding.'
                : fundingData?.status === 'needs_work'
                ? 'Your credit profile needs some improvements. Address the blockers below to increase your approval chances.'
                : 'Your credit profile has significant issues that may prevent funding approval. Focus on resolving the critical blockers.'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Blockers */}
        <div className="elite-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="text-red-500" size={20} />
            <h3 className="text-lg font-semibold text-stone-900">Funding Blockers</h3>
          </div>
          {fundingData?.blockers && fundingData.blockers.length > 0 ? (
            <div className="space-y-3">
              {fundingData.blockers.map((blocker, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-red-50 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle size={14} className="text-red-600" />
                  </div>
                  <span className="text-sm text-red-800">{blocker}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg">
              <CheckCircle className="text-emerald-600" size={20} />
              <span className="text-emerald-700">No critical blockers found!</span>
            </div>
          )}
        </div>

        {/* Strengths */}
        <div className="elite-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="text-emerald-500" size={20} />
            <h3 className="text-lg font-semibold text-stone-900">Profile Strengths</h3>
          </div>
          {fundingData?.strengths && fundingData.strengths.length > 0 ? (
            <div className="space-y-3">
              {fundingData.strengths.map((strength, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={14} className="text-emerald-600" />
                  </div>
                  <span className="text-sm text-emerald-800">{strength}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-stone-50 rounded-lg text-center text-stone-500">
              Import your credit report to see strengths
            </div>
          )}
        </div>
      </div>

      {/* Next Steps */}
      <div className="elite-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Target className="text-amber-500" size={20} />
            <h3 className="text-lg font-semibold text-stone-900">Next Steps</h3>
          </div>
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">AI Recommended</span>
        </div>
        {fundingData?.next_steps && fundingData.next_steps.length > 0 ? (
          <div className="space-y-3">
            {fundingData.next_steps.map((step, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
                onClick={() => navigate('/disputes')}
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-amber-600">{index + 1}</span>
                  </div>
                  <span className="text-stone-700">{step}</span>
                </div>
                <ChevronRight size={18} className="text-stone-400" />
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-stone-50 rounded-lg text-center text-stone-500">
            Import your credit report to get personalized next steps
          </div>
        )}
      </div>

      {/* Strategy Recommendations */}
      {fundingData?.strategy?.recommendations && (
        <div className="elite-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-amber-500" size={20} />
            <h3 className="text-lg font-semibold text-stone-900">AI Funding Strategy</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fundingData.strategy.recommendations.map((rec, index) => (
              <div 
                key={index}
                className="p-4 border border-stone-200 rounded-xl hover:border-emerald-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-stone-900">{rec.title}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    rec.impact === 'High' ? 'bg-red-100 text-red-700' :
                    rec.impact === 'Medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-stone-100 text-stone-700'
                  }`}>
                    {rec.impact} Impact
                  </span>
                </div>
                <p className="text-sm text-stone-500">{rec.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="elite-card p-6 bg-gradient-to-r from-emerald-50 to-amber-50 border-emerald-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-stone-900">Ready to improve your funding chances?</h3>
            <p className="text-stone-600 mt-1">Start by addressing the blockers and following the AI recommendations.</p>
          </div>
          <Button onClick={() => navigate('/disputes')} className="btn-primary">
            Start Disputes
            <ChevronRight size={18} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FundingReadiness;
